import { useState } from "react";
import { ChevronRight, ClipboardList, History } from "lucide-react";
import type { Schema } from "./api";
import { kindLabels } from "./svor";
import {
  svorActionLabels, svorActionTopics, svorProtocolEntries, svorRemarkAttention, svorRemarkContext,
  svorRemarkQuantity, type SvorActionFilter,
} from "./svorAttention";
import "./svor-actions.css";

type Props = { round: Pick<Schema["SvorRoundOut"], "remarks" | "carried" | "number">; latest: boolean };

/** Read-only view of the same immutable protocol; no decisions, calculations or source changes. */
export function SvorActionProtocol({ round, latest }: Props) {
  const [filter, setFilter] = useState<SvorActionFilter>("all");
  const entries = svorProtocolEntries(round);
  const current = entries.filter(entry => !entry.previous);
  const previous = entries.filter(entry => entry.previous);
  const count = (value: SvorActionFilter) => value === "all" ? entries.length : value === "previous" ? previous.length
    : current.filter(entry => svorRemarkAttention(entry.remark).topic === value).length;
  const active = count(filter) ? filter : "all";
  const filters = [{ key: "all" as const, label: "Все записи" },
    ...svorActionTopics.filter(topic => count(topic)).map(topic => ({ key: topic, label: svorActionLabels[topic] })),
    ...(previous.length ? [{ key: "previous" as const, label: "Из прошлого раунда" }] : [])];
  return <div className={`svor-action-protocol${latest ? "" : " sap-snapshot"}`}>
    <p className="sap-intro">{latest
      ? "Сначала проверьте указанное основание в файле. Исправления источников попадут в новый раунд; этот результат не изменяется."
      : `Сохранённый раунд ${round.number}: метки описывают замечания на момент расчёта, а не текущее состояние проекта.`}
      {" "}Подтверждать каждую позицию здесь не требуется.</p>
    <div className="sap-filters" role="group" aria-label="Замечания СВОР по действию">
      {filters.map(item => <button type="button" key={item.key} aria-pressed={active === item.key} onClick={() => setFilter(item.key)}>
        {item.label}<span>{count(item.key)}</span>
      </button>)}
    </div>
    {([false, true] as const).map(carried => {
      const visible = (carried ? previous : current).filter(entry => active === "all" || (carried ? active === "previous" : svorRemarkAttention(entry.remark).topic === active));
      if (!visible.length) return null;
      const Icon = carried ? History : ClipboardList;
      const title = carried ? "Из прошлого раунда" : "Замечания источников раунда";
      return <section key={title} className={`sap-group${carried ? " sap-previous" : ""}`} aria-label={title}>
        <header className="sap-group-heading"><Icon size={18} aria-hidden="true" /><h4>{title}</h4><span>{visible.length}</span></header>
        {carried && <p className="sap-group-note">Эти замечания сохранены из прошлого раунда. Исчезновение позиции не означает, что вопрос устранён.</p>}
        {visible.map(({ id, remark }) => {
          const item = svorRemarkAttention(remark);
          const tone = carried || !latest ? "neutral" : item.tone;
          return <details className="sap-row" key={id}>
            <summary><ChevronRight size={18} className="sap-chevron" aria-hidden="true" />
              <span className="sap-subject"><small>{kindLabels[remark.source_kind]}{remark.position.number ? ` · позиция ${remark.position.number}` : " · без номера"}</small>
                <strong>{remark.position.description || remark.label || "Замечание без наименования позиции"}</strong>
                <span className={`sap-action sap-action--${tone}`}>{latest ? "Что сделать: " : "В снимке: "}{item.action}</span>
                <span className="sap-explanation">{remark.label} · {item.explanation}</span>
              </span>
              <span className="sap-values"><small>Объём в замечании</small><b>{svorRemarkQuantity(remark)}</b><small>{remark.rows.length ? `Строк источника: ${remark.rows.length}` : "Адрес строки не указан"}</small></span>
              <span className="sap-detail-label">Основание</span>
            </summary>
            <div className="sap-body"><p><strong>{remark.position.description || remark.label || "Замечание без наименования позиции"}</strong></p><p className="sap-context">{svorRemarkContext(carried, latest)}</p>
              {[remark.position.section, remark.position.subsection].some(Boolean) && <p className="muted">{[remark.position.section, remark.position.subsection].filter(Boolean).join(" / ")}</p>}
              <p>{remark.message}</p>
              {remark.rows.length > 0 && <ul className="sap-sources">{remark.rows.map((row, index) => <li key={`${row.source_id}:${row.row_number}:${index}`}>
                <a href={`/api/v1/svor-sources/${row.source_id}/download`} download>{row.filename || "Файл источника"} · строка {row.row_number}</a>
              </li>)}</ul>}
            </div>
          </details>;
        })}
      </section>;
    })}
  </div>;
}
